import type { CampaignReading } from '../shared/campaigns';

const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const number = (value: number) => value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

export function ReadingHistory({ readings }: { readings: CampaignReading[] }) {
  if (!readings.length) return null;
  const [latest, previous] = readings;
  const comparable =
    previous &&
    latest.apiVersion === previous.apiVersion &&
    latest.kind === previous.kind &&
    latest.windowStart === previous.windowStart &&
    latest.source === previous.source;
  return (
    <details className="cw-reading-history">
      <summary>Histórico de leituras · {readings.length}</summary>
      {comparable && (
        <div className="cw-reading-comparison">
          <h4>Variação do acumulado</h4>
          <p>
            De {stamp(previous.collectedAt)} a {stamp(latest.collectedAt)}. A variação mostra a
            mudança entre os dois registros; pode incluir correções do provedor.
          </p>
          {latest.metrics.map((metric) => {
            const before = previous.metrics.find(
              (value) =>
                value.key === metric.key &&
                value.unit === metric.unit &&
                value.aggregation === metric.aggregation,
            );
            if (
              metric.status !== 'available' ||
              metric.value === null ||
              !before ||
              before.status !== 'available' ||
              before.value === null
            )
              return null;
            const delta = metric.value - before.value;
            return (
              <div className="cw-reading-change" key={metric.key}>
                <span>{metric.label}</span>
                <span>
                  {number(before.value)} → {number(metric.value)}
                </span>
                <strong>
                  {delta > 0 ? '+' : ''}
                  {number(delta)}
                  {metric.unit === 'milliseconds' ? ' ms' : ''}
                </strong>
              </div>
            );
          })}
        </div>
      )}
      {readings.map((reading) => (
        <article className="cw-reading-entry" key={reading.id}>
          <strong>{stamp(reading.collectedAt)}</strong>
          <p>
            Meta · {reading.apiVersion} · acumulado desde a publicação · idade:{' '}
            {number(reading.ageSeconds / 86400)} dias.
          </p>
          {reading.collectionKind === 'd7' && reading.collectionDueAt && (
            <p>
              Fechamento D+7 previsto para {stamp(reading.collectionDueAt)}. Coletado{' '}
              {number((reading.delaySeconds ?? 0) / 60)} min depois; os valores acumulam até a
              coleta efetiva.
            </p>
          )}
          <div className="cw-metric-list">
            {reading.metrics.map((metric) => (
              <div key={metric.key}>
                <span>{metric.label}</span>
                <strong>
                  {metric.status === 'available' && metric.value !== null
                    ? `${number(metric.value)}${metric.unit === 'milliseconds' ? ' ms' : ''}`
                    : '—'}
                </strong>
                {metric.status !== 'available' && (
                  <small>
                    {metric.error ||
                      metric.description ||
                      (metric.status === 'error' ? 'Falha na coleta' : 'Indisponível na fonte')}
                  </small>
                )}
              </div>
            ))}
          </div>
        </article>
      ))}
    </details>
  );
}
